import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getSupabase } from "./supabase";
import type { User as SupabaseUser, Session } from "@supabase/supabase-js";

export type Role = "staff" | "manager" | "admin" | "client" | "contractor";

export interface User {
  id: string;
  email: string;
  name: string;
  avatar: string;
  role: Role;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signUp: (email: string, password: string, name: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  getToken: () => Promise<string | null>;
  resetPassword: (email: string) => Promise<{ error?: string }>;
  tryReauth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const IDLE_MS = 9 * 60 * 60 * 1000;
const ACTIVITY_KEY = "centcom_last_activity";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = useCallback(async (accessToken: string): Promise<boolean> => {
    try {
      const response = await fetch("/api/me", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const profile = await response.json();
        setUser(profile);
        return true;
      } else if (response.status === 401) {
        // Before destroying the session, try to refresh — the token may just be stale,
        // not genuinely revoked. Only sign out hard if the refresh also fails.
        const supabase = await getSupabase();
        const { data: { session: refreshed } } = await supabase.auth.refreshSession();
        if (refreshed) {
          const retry = await fetch("/api/me", {
            headers: { Authorization: `Bearer ${refreshed.access_token}` },
          });
          if (retry.ok) {
            setSession(refreshed);
            setUser(await retry.json());
            return true;
          }
        }
        // Refresh failed or retry still 401 — token is genuinely invalid (revoked, password changed)
        await supabase.auth.signOut();
        setUser(null);
        setSession(null);
        return false;
      }
      return false;
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
      return false;
    }
  }, []);

  /**
   * Attempt Cloudflare Zero Trust auto-login.
   * If the user accessed via crm.ccpas.net, Cloudflare headers will be present
   * and we can create a Supabase session without showing a login form.
   */
  const attemptCfAutoLogin = useCallback(async (): Promise<boolean> => {
    try {
      // First check if Zero Trust auth is present
      const checkResponse = await fetch("/api/auth/cf-user");
      if (!checkResponse.ok) {
        return false; // No Cloudflare auth, fall back to normal login
      }

      const { authenticated } = await checkResponse.json();
      if (!authenticated) {
        return false;
      }

      // Request a session token from the server
      const sessionResponse = await fetch("/api/auth/cf-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });

      if (!sessionResponse.ok) {
        console.error("CF session request failed");
        return false;
      }

      const { token, type } = await sessionResponse.json();

      if (!token) {
        console.error("No token in CF session response");
        return false;
      }

      // Use the token to verify OTP and establish session
      const supabase = await getSupabase();
      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: token,
        type: type || "magiclink"
      });

      if (error) {
        console.error("OTP verification failed:", error);
        return false;
      }

      if (data.session) {
        setSession(data.session);
        await fetchUserProfile(data.session.access_token);
        return true;
      }

      return false;
    } catch (error) {
      console.error("CF auto-login error:", error);
      return false;
    }
  }, [fetchUserProfile]);

  useEffect(() => {
    let mounted = true;
    let subscription: { unsubscribe: () => void } | null = null;

    const initAuth = async () => {
      try {
        const supabase = await getSupabase();

        // Always register onAuthStateChange so token refreshes keep React state current.
        // Without this, the session stored in React state becomes stale after ~1 hour
        // and getToken() returns an expired JWT, causing "Invalid token" errors.
        const { data: { subscription: sub } } = supabase.auth.onAuthStateChange(
          async (event, newSession) => {
            if (!mounted) return;
            setSession(newSession);
            if (!newSession || event === "SIGNED_OUT") {
              setUser(null);
              return;
            }
            // TOKEN_REFRESHED: SDK refreshed the JWT automatically — session is updated above,
            // but the profile hasn't changed, so skip the /api/me call. Calling it here risks
            // a transient 401 incorrectly triggering signOut and logging the user out.
            if (event === "TOKEN_REFRESHED") return;
            // SIGNED_IN fires only for interactive sign-ins (password, magic link, OTP) —
            // a restored session at boot fires INITIAL_SESSION instead. An interactive
            // sign-in is proof of presence, so reset the idle clock here; otherwise the
            // stale timestamp from a previous session signs the user right back out.
            if (event === "SIGNED_IN") {
              localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
            }
            // SIGNED_IN, INITIAL_SESSION, PASSWORD_RECOVERY — fetch profile
            await fetchUserProfile(newSession.access_token);
          }
        );
        subscription = sub;

        // Check for existing session to populate state immediately on load
        const { data: { session: currentSession } } = await supabase.auth.getSession();

        if (mounted && currentSession) {
          setSession(currentSession);
          await fetchUserProfile(currentSession.access_token);
          setLoading(false);
          return;
        }

        // No existing session - try Cloudflare Zero Trust auto-login
        const cfLoginSuccess = await attemptCfAutoLogin();

        if (mounted) {
          setLoading(false);
        }
      } catch (error) {
        console.error("Auth init error:", error);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initAuth();

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, [fetchUserProfile, attemptCfAutoLogin]);

  const signIn = async (email: string, password: string) => {
    try {
      const supabase = await getSupabase();
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { error: error.message };
      return {};
    } catch (error: any) {
      return { error: error.message || "Sign in failed" };
    }
  };

  const signUp = async (email: string, password: string, name: string) => {
    try {
      const supabase = await getSupabase();
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { name, avatar: name.substring(0, 2).toUpperCase() },
        },
      });
      if (error) return { error: error.message };
      return {};
    } catch (error: any) {
      return { error: error.message || "Sign up failed" };
    }
  };

  const signOut = async () => {
    const supabase = await getSupabase();
    await supabase.auth.signOut();
    localStorage.removeItem(ACTIVITY_KEY);
    setUser(null);
    setSession(null);
  };

  // ── Idle auto-logout (9 hours of inactivity) ──────────────────────────────
  useEffect(() => {
    if (!user) return;

    // On mount: if a stale timestamp exists, sign out immediately (handles closed-tab idle)
    const existing = Number(localStorage.getItem(ACTIVITY_KEY) ?? 0);
    if (existing > 0 && Date.now() - existing > IDLE_MS) {
      signOut();
      return;
    }
    // Passed the staleness check — loading the app is itself activity, so always
    // re-stamp. A near-stale leftover (e.g. 8h50m old) must not survive to trip
    // the interval check minutes after a successful login.
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));

    const events = ["mousemove", "keydown", "touchstart", "click"] as const;
    // Throttle: only write to localStorage at most once per minute
    let lastWrite = Date.now();
    const handler = () => {
      const now = Date.now();
      if (now - lastWrite > 60_000) { lastWrite = now; localStorage.setItem(ACTIVITY_KEY, String(now)); }
    };
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));

    const check = setInterval(() => {
      const last = Number(localStorage.getItem(ACTIVITY_KEY) ?? 0);
      if (Date.now() - last > IDLE_MS) signOut();
    }, 5 * 60 * 1000);

    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      clearInterval(check);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const getToken = async () => {
    return session?.access_token || null;
  };

  const resetPassword = async (email: string) => {
    try {
      const res = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return { error: (data as any).message || "Password reset failed" };
      }
      return {};
    } catch (error: any) {
      return { error: error.message || "Password reset failed" };
    }
  };

  // Try to silently re-authenticate (CF Zero Trust auto-login, then Supabase refresh).
  // Returns true if a valid session was recovered.
  const tryReauth = useCallback(async (): Promise<boolean> => {
    try {
      const supabase = await getSupabase();
      const { data: { session: current } } = await supabase.auth.getSession();
      if (current) {
        setSession(current);
        return await fetchUserProfile(current.access_token);
      }
      // No current session — try Supabase token refresh
      const { data: { session: refreshed } } = await supabase.auth.refreshSession();
      if (refreshed) {
        setSession(refreshed);
        return await fetchUserProfile(refreshed.access_token);
      }
      // Fall back to CF Zero Trust auto-login
      return await attemptCfAutoLogin();
    } catch {
      return false;
    }
  }, [fetchUserProfile, attemptCfAutoLogin]);

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signOut, getToken, resetPassword, tryReauth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
